/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Button, Typography as MuiTypography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Loader } from "../../../../components/Loader";
import Modal from "../../../../components/Modal";
import Typography from "../../../../components/Typography";
import apiClient from "../../../../services/api-client";
import TestIds from "../../../../testIds";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import { InlineFlex } from "../../../cart/components/CartSummaryComponents";
import { CancelQuotePayload, Quote } from "../../storage/types";

interface QuoteCancelModalProps {
  open: boolean;
  handleClose: () => void;
  quote: Quote | null;
}

export const QuoteCancelModal: React.FC<QuoteCancelModalProps> = ({ open, handleClose, quote }) => {
  const account = useSelector(selectCurrentFarmerAccount);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const cancel = async () => {
    try {
      setSubmitting(true);
      const result = await apiClient.post<Quote, CancelQuotePayload>(
        `/quotes/${quote?.id}/cancel`,
        {
          accountNumber: String(account?.accountNumber),
          version: String(quote?.version),
        }
      );
      if (result && result.status === 200 && result.data) {
        // notify('Quote Rejected')
        navigate("/quotes");
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Cancel Quote?"
      open={open}
      handleClose={handleClose}
      header={
        <Typography>
          The quote will be removed from your account managers request list and you will not receive
          an offer.
        </Typography>
      }
      footer={
        submitting ? (
          <Loader type="inline-flex" title="Submitting" />
        ) : (
          <InlineFlex
            sx={{
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                textDecoration: "underline",
                color: "#00553D",
                fontWeight: "bold",
                cursor: "pointer",
              }}
              onClick={handleClose}
            >
              Keep quote
            </Typography>
            <Button
              disabled={submitting}
              variant="contained"
              // sx={{ width: '100%' }}
              onClick={cancel}
              data-testid={TestIds.feat.quotes.cancelOffer.cancel}
            >
              Cancel Quote
            </Button>
          </InlineFlex>
        )
      }
    >
      {error && (
        <MuiTypography variant="body2" color="error" sx={{ marginTop: "8px" }}>
          Something went wrong, please try again.
        </MuiTypography>
      )}
    </Modal>
  );
};
